// leadPriority — pure client-side priority bucket computer.
// Mirrors the server logic in api/leads/morning-brief.js so any
// row / card render can color itself without a network round trip.
//
// Rules (identical to server):
//   dead        — notes contain not-interested / opt-out
//   won         — status: sold | closed
//   call_now    — followUp overdue OR probability >= 0.70
//   call_today  — followUp within 1d OR probability >= 0.50 OR score >= 60
//   at_risk     — no touch in > 30d
//   follow_up   — no touch in 8-30d
//   nurturing   — everything else

const DAY_MS = 86_400_000;

export function calcLeadScore5(lead) {
  const notes = String(lead?.notes || '').toLowerCase();
  let s = 1;
  if (/\$500\+|\$500 \+|500\s?\+/.test(notes)) s += 2;
  else if (/\$300\s?[-–]\s?\$?499|300\s?[-–]\s?499/.test(notes)) s += 1;
  if (/financial freedom|\bboth\b/.test(notes)) s += 1;
  if (/(working\s*-?\s*w2|w-?2\b|self-?employed)/.test(notes)) s += 1;
  const ageMatch = notes.match(/(?:age|aged?)[: ]+(\d{2})|\b(\d{2})\s*(?:yo|y\/o|years old)/);
  const ageVal = ageMatch ? Number(ageMatch[1] || ageMatch[2]) : null;
  if (ageVal != null && ageVal >= 35 && ageVal <= 55) s += 1;
  const created = lead?.createdAt ? new Date(lead.createdAt).getTime() : 0;
  const days = created > 0 ? Math.floor((Date.now() - created) / DAY_MS) : Infinity;
  if (days <= 7) s += 1;
  else if (days > 30) s -= 1;
  return Math.max(1, Math.min(5, s));
}

export function calcLeadScore100(lead) {
  return calcLeadScore5(lead) * 20;
}

function probabilityOfClose(lead, callHistory = [], outreachLog = []) {
  const stageBase = {
    new: 0.10, contacted: 0.20, interested: 0.35, appt_set: 0.55,
    quoted: 0.65, application: 0.75, sold: 1.00, lost: 0.00,
  };
  const base = stageBase[lead?.status] ?? 0.15;
  let p = base;
  if (callHistory.length >= 2) p += 0.05;
  if (callHistory.length >= 5) p += 0.05;
  if (outreachLog.length >= 3) p += 0.03;
  const recentEngaged = outreachLog.some(o => {
    const t = Date.parse(o.at || o.created_at || 0);
    if (!Number.isFinite(t)) return false;
    return Date.now() - t < 7 * DAY_MS;
  });
  if (recentEngaged) p += 0.05;
  const notes = String(lead?.notes || '').toLowerCase();
  if (/(ready|start now|sign up|enroll)/.test(notes)) p += 0.08;
  if (/(not interested|remove|do not call)/.test(notes)) p -= 0.30;
  return Math.max(0, Math.min(1, p));
}

export function computeLeadPriority(lead, callHistory = [], outreachLog = []) {
  const now = Date.now();
  const notes = String(lead?.notes || '').toLowerCase();
  if (/(not interested|remove|do not call|opt.?out)/.test(notes)) return 'dead';
  if (lead?.status === 'sold' || lead?.status === 'closed') return 'won';
  if (lead?.status === 'lost') return 'dead';

  if (lead?.followUpDate) {
    const days = (Date.parse(lead.followUpDate) - now) / DAY_MS;
    if (days <= 0) return 'call_now';
    if (days <= 1) return 'call_today';
  }

  const score = calcLeadScore100(lead);
  const prob  = probabilityOfClose(lead, callHistory, outreachLog);

  if (prob >= 0.70) return 'call_now';
  if (prob >= 0.50) return 'call_today';
  if (score >= 60)  return 'call_today';

  const lastTouch = [...(callHistory), ...(outreachLog)].sort((a, b) =>
    String(b.at || b.created_at || '').localeCompare(String(a.at || a.created_at || ''))
  )[0];
  const daysSince = lastTouch
    ? Math.floor((now - Date.parse(lastTouch.at || lastTouch.created_at || 0)) / DAY_MS)
    : Infinity;
  if (daysSince > 30) return 'at_risk';
  if (daysSince > 7)  return 'follow_up';
  return 'nurturing';
}
